<script setup lang="ts">
  import SearchIcon from '@/components/icons/SearchIcon.vue'
  import { Switch } from '@/components/ui/switch'
  import {
    AiAugmentedByMapperKeysEnum,
    type CustomerCase as Case,
    type GenericDataMapper
  } from '@/types/generated/types.gen'
  import '@tato30/vue-pdf/style.css'
  import { CheckCircle, Loader2 } from 'lucide-vue-next'
  import { computed, ref } from 'vue'

  import DatamapperTable from '@/components/app/cases/DatamapperTable.vue'
  import DocumentProcessingStatus from '@/components/app/cases/documents/DocumentProcessingStatus.vue'
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { debounce } from '@/lib/timing'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import HelpLink from '@/components/app/HelpLink.vue'
  import { KNOWLEDGE_BASE_URLS } from '@/constants/knowledgeBaseUrls'
  import { DocumentTextIcon } from '@heroicons/vue/24/outline'
  import { ROUTES } from '@/router/routes'
  import LawSpecificDataCalculator from '@/components/app/cases/law_specific_data/LawSpecificDataCalculator.vue'

  const { t } = useTranslation({
    en: {
      loadingData: 'Loading data',
      savingData: 'Saving data',
      updatingData: 'Updating data',
      updatedFields: 'Updated fields:',
      searchField: 'Search for field',
      nurFelderMitWertenAnzeigen: 'Show only fields with values',
      DocumentsBeingAnalyzedDataUpdate:
        'Documents are being analyzed, data will be updated soon. You can leave the tab and review the data later.',
      AllDocumentsAnalyzed: 'All documents analyzed',
      lawRelevantData: 'Law Relevant Data',
      helpLawRelevantData: 'Learn more about law relevant data',
      documentGenerate: 'Generate document',
      aiBadge: 'AI'
    },
    de: {
      loadingData: 'Lade Daten',
      savingData: 'Daten werden gespeichert',
      updatingData: 'Daten werden aktualisiert',
      updatedFields: 'Aktualisierte Felder:',
      searchField: 'Suche nach Feld',
      nurFelderMitWertenAnzeigen: 'Nur Felder mit Werten anzeigen',
      DocumentsBeingAnalyzedDataUpdate:
        'Dokumente werden analysiert, Daten werden bald aktualisiert. Sie können den Tab verlassen und die Daten später überprüfen.',
      AllDocumentsAnalyzed: 'Alle Dokumente analysiert',
      lawRelevantData: 'Rechtsspezifische Daten',
      helpLawRelevantData: 'Mehr über rechtsspezifische Daten erfahren',
      documentGenerate: 'Dokument generieren',
      aiBadge: 'KI'
    }
  })

  const caseStore = useCaseStore()
  const { toast } = useToast()
  const { trackEvent } = useMetrics()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const nonEmptyMappers = computed<GenericDataMapper[]>(() => caseStore.nonEmptyLawSpecificMappers)
  const lawSpecificMappers = computed<GenericDataMapper[]>(() => caseStore.lawSpecificMappers)

  // Get document processing data for DocumentProcessingStatus
  const documentProcessor = caseStore.documentProcessor

  const showMappersWithValues = ref(true)
  const search = ref('')

  const filteredMappers = computed<GenericDataMapper[]>(() => {
    const mappers = showMappersWithValues?.value ? nonEmptyMappers : lawSpecificMappers
    return mappers.value?.filter((mapper) => {
      return mapper.label.toLowerCase().includes(search.value.toLowerCase())
    })
  })

  const handleSwitchChange = (value: boolean) => {
    showMappersWithValues.value = value
  }

  const shouldDisplayMapperKeys = computed<string[]>(() => {
    return filteredMappers.value.map((mapper) => mapper.key)
  })

  const handleSave = debounce(async (key: string, mapperKey: string, value: any) => {
    console.log('handling save', key, mapperKey, value)
    await caseStore.saveDataWithMapperKey(mapperKey as AiAugmentedByMapperKeysEnum, value)
    // Show toast message about the updated fields
    const mapperLabel = caseRecord.value.data.mappers.find((m) => m.key === mapperKey)?.label
    toast({
      title: t('updatedFields'),
      description: mapperLabel ?? mapperKey,
      variant: 'default'
    })
  }, 1000)
</script>

<template>
  <!-- Document Processing Status -->
  <DocumentProcessingStatus
    :use-slots="true"
    :documents="documentProcessor.documents"
    :processing-documents="documentProcessor.processingDocuments"
    :active-uploads="documentProcessor.activeUploads"
    :documents-with-errors="documentProcessor.documentsWithErrors"
  >
    <template #default="{ hasActivity, showSuccessMessage }">
      <div
        v-if="hasActivity || showSuccessMessage"
        class="mt-2 flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm"
        :class="[
          hasActivity
            ? 'border border-purple-200 bg-purple-50 text-purple-700'
            : 'border border-green-200 bg-green-50 text-green-700'
        ]"
      >
        <div class="flex items-center gap-2">
          <Loader2
            v-if="hasActivity"
            class="h-4 w-4 animate-spin text-purple-500"
          />
          <CheckCircle
            v-else
            class="h-4 w-4 text-green-500"
          />
          <span>
            {{ hasActivity ? t('DocumentsBeingAnalyzedDataUpdate') : t('AllDocumentsAnalyzed') }}
          </span>
        </div>
      </div>
    </template>
  </DocumentProcessingStatus>

  <!-- Header with title and buttons -->
  <div
    v-if="caseRecord"
    class="mt-4 flex items-center justify-between"
  >
    <!-- Left side: Title + HelpLink -->
    <div class="flex items-center gap-2">
      <h2 class="text-lg font-semibold text-gray-900">{{ t('lawRelevantData') }}</h2>
      <HelpLink
        :url="KNOWLEDGE_BASE_URLS.lawRelevantData"
        :tooltip="t('helpLawRelevantData')"
        location="law_relevant_data"
      />
    </div>

    <!-- Right side: Buttons -->
    <div class="flex items-center gap-2">
      <a
        v-if="caseRecord.uuid"
        :href="`${ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION.replace(':uuid', caseRecord.uuid)}?source=law_specific_data`"
        class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
          font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
          disabled:pointer-events-none disabled:opacity-50"
        @click="
          trackEvent(MetricEventType.AI_FUNCTION_DOCUMENT_GENERATION_USED, {
            source: 'law_specific_data'
          })
        "
      >
        <DocumentTextIcon class="h-5 w-5" />
        {{ t('documentGenerate') }}
        <span class="badge bg-purple">{{ t('aiBadge') }}</span>
      </a>
      <LawSpecificDataCalculator />
    </div>
  </div>

  <div
    class="mt-4 flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700
      dark:bg-neutral-900 dark:text-neutral-400 md:p-5"
  >
    <div>
      <div class="grid gap-y-2 pb-5 ps-1 pt-1 md:grid-cols-2 md:gap-x-5 md:gap-y-0">
        <div>
          <!-- Search Input -->
          <div class="relative">
            <div
              class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5"
            >
              <SearchIcon class="size-4 flex-shrink-0 text-gray-500" />
            </div>
            <input
              v-model="search"
              data-cy="lawSpecificDataSearch"
              class="block w-full rounded-lg border-transparent bg-gray-100 px-3 py-[7px] ps-10 text-sm
                focus:border-purple-500 focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
              type="text"
              :placeholder="t('searchField')"
            />
          </div>
          <!-- End Search Input -->
        </div>
        <div
          class="flex items-center justify-between"
          :class="showMappersWithValues ? '' : ''"
        >
          <div></div>
          <div class="flex items-center space-x-4">
            <div class="flex items-center justify-end pr-2">
              <slot name="top-actions"></slot>
            </div>
            <div class="flex items-center space-x-2">
              <label class="text-sm text-gray-700">{{ t('nurFelderMitWertenAnzeigen') }}</label>
              <Switch
                v-model="showMappersWithValues"
                :checked="showMappersWithValues"
                data-cy="lawSpecificDataToggle"
                class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
                thumb-class=""
                @update:checked="handleSwitchChange"
              />
            </div>
          </div>
        </div>
        <!-- End Col -->
      </div>
      <DatamapperTable
        :show-keys="shouldDisplayMapperKeys"
        :object="caseRecord"
        :is-entity="false"
        :mappers="caseRecord?.data.mappers"
        :mode="'edit'"
        data-cy="lawSpecificDataTable"
        @on-value-change="handleSave"
      />
    </div>
  </div>
</template>
